import { PUNCHOUT_USER_EXTRA_KEY } from '../models/punchout';
import { ROLE_PUNCHOUT } from '../models/user';
import { User } from '../state/auth/types';

/**
 * Which punchout config a user is wired to, or undefined for a user that is not. The link lives in
 * the untyped `extra` bag rather than on the User interface, written by UserDetailsPunchoutComponent
 * and read backend-side by PunchoutUserConfigResolver.
 */
export function getPunchoutConfigId(user: User): string | undefined {
  const value = user?.extra?.[PUNCHOUT_USER_EXTRA_KEY];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/** Latest successful login, preferring the loginData block and falling back to the flat field. */
export function getPunchoutUserLatestLogin(user: User): string | undefined {
  return user?.extra?.loginData?.latestSuccessfulLogin ?? user?.extra?.latestLogin ?? undefined;
}

/**
 * Users wired to one config, active first and then most recently logged in, so a config nobody has
 * used is visibly stale. Matching is an exact id comparison: a user pointing at a config that no
 * longer exists belongs to unconnectedPunchoutUsers instead, not to some other config.
 */
export function connectedPunchoutUsers(users: User[], configId?: string): User[] {
  if (!configId) {
    return [];
  }
  return users
    .filter(user => getPunchoutConfigId(user) === configId)
    .sort((a, b) => {
      if (a.active !== b.active) {
        return a.active ? -1 : 1;
      }
      return (getPunchoutUserLatestLogin(b) ?? '').localeCompare(getPunchoutUserLatestLogin(a) ?? '');
    });
}

/**
 * Punchout users no config on the page accounts for, in two flavours: a user whose stored config id
 * points at a config that has since been deleted, and a PUNCHOUT-role user with no config id at all
 * (who falls through to the default redirect). Both are invisible on every config row, which is why
 * they are surfaced separately.
 */
export function unconnectedPunchoutUsers(users: User[], configIds: string[]): User[] {
  const known = new Set(configIds);
  return users.filter(user => {
    const configId = getPunchoutConfigId(user);
    if (configId) {
      return !known.has(configId);
    }
    return (user.roles || []).includes(ROLE_PUNCHOUT);
  });
}

/**
 * The OCI login URL to hand the buyer's procurement system for one user. HOOK_URL is deliberately
 * omitted even though the backend declares it non-optional: the buyer's system supplies it per
 * session, so this is a spec sheet for them rather than a link that can be tested as-is. The
 * password is a placeholder because getAllUsers blanks it server-side.
 */
export function punchoutExampleLoginUrl(loginUrl: string, username: string): string {
  return `${loginUrl}?USERNAME=${encodeURIComponent(username)}&PASSWORD=<PASSWORD>`;
}
